// Which rows changed between one snapshot of a list and the next.
//
// The first list the hook sees (null means "not loaded yet") is only remembered, never
// highlighted. After that, an id that is new or whose signature differs is reported for
// `HIGHLIGHT_MS`. A further change to the same id restarts its window and bumps its
// number, so the row can restart its CSS animation (it alternates between two of them by
// the number's parity).

import { useEffect, useRef, useState } from 'react';

export const HIGHLIGHT_MS = 6000;

/** Row id -> how many times that row has been highlighted; absent when not highlighted. */
export type ChangedIds = ReadonlyMap<string, number>;

export function useChangedIds<T extends { readonly id: string }>(
  items: readonly T[] | null,
  signature: (item: T) => string,
): ChangedIds {
  const previous = useRef<Map<string, string> | null>(null);
  const counts = useRef(new Map<string, number>());
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const [changed, setChanged] = useState<ChangedIds>(new Map());

  useEffect(() => {
    if (items === null) {
      return;
    }
    const next = new Map(items.map((item) => [item.id, signature(item)]));
    const before = previous.current;
    previous.current = next;
    if (before === null) {
      return;
    }
    const hits = [...next]
      .filter(([id, sig]) => before.get(id) !== sig)
      .map(([id]) => id);
    if (hits.length === 0) {
      return;
    }
    for (const id of hits) {
      counts.current.set(id, (counts.current.get(id) ?? 0) + 1);
      clearTimeout(timers.current.get(id));
      timers.current.set(
        id,
        setTimeout(() => {
          timers.current.delete(id);
          setChanged((now) => {
            const rest = new Map(now);
            rest.delete(id);
            return rest;
          });
        }, HIGHLIGHT_MS),
      );
    }
    setChanged((now) => {
      const more = new Map(now);
      for (const id of hits) {
        more.set(id, counts.current.get(id) ?? 1);
      }
      return more;
    });
    // `signature` is a stable per-page function; only the list drives this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(
    () => () => {
      for (const timer of timers.current.values()) {
        clearTimeout(timer);
      }
    },
    [],
  );

  return changed;
}

/** The class for a highlighted row, or '' — two names alternate so a repeat restarts. */
export function changedClass(changed: ChangedIds, id: string): string {
  const n = changed.get(id);
  return n === undefined
    ? ''
    : n % 2 === 0
      ? 'rowChanged rowChanged--b'
      : 'rowChanged rowChanged--a';
}
